import { ArrowLeftOutlined, EditOutlined, SettingOutlined } from "@ant-design/icons"
import { Button, Flex, Skeleton, Typography, message } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { Navigate, useNavigate, useParams } from "react-router-dom"

import { ActionTooltip } from "../../../../components/ActionTooltip.tsx"
import { neutral } from "../../../../components/widgets/tokens.ts"
import { generateDocumentTitle } from "../../../../constants/document-title.ts"
import { RoutesPaths } from "../../../../constants/routes.ts"
import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import type { RootState } from "../../../../store.ts"
import { normalizeDefinition } from "../../helpers/reportDefinition.ts"
import { distinctFolderNames } from "../../helpers/reportFolders.ts"
import { reportIconKeyOf, ReportIconGlyph } from "../../helpers/reportIcons.tsx"
import { useReportCanvasAutosave } from "../../hooks/useReportCanvasAutosave.ts"
import { useReportSettingsSubmit } from "../../hooks/useReportSettingsSubmit.ts"
import {
  reportCanvasHydrate,
  reportCanvasReset
} from "../../redux/reportCanvas.slice.ts"
import { reportsClearDetail } from "../../redux/reports.slice.ts"
import {
  fetchReportDetails,
  fetchReports,
  renameReport
} from "../../redux/reports.thunks.ts"
import { ReportSaveIndicator } from "../canvas/ReportSaveIndicator.tsx"
import { ReportScheduleDrawer } from "../canvas/ReportScheduleDrawer.tsx"
import { ReportShareModal } from "../canvas/ReportShareModal.tsx"
import { ReportShareScheduleActions } from "../canvas/ReportShareScheduleActions.tsx"
import { ReportSettingsModal } from "../ReportSettingsModal.tsx"
import { ReportBuildLayout } from "./ReportBuildLayout.tsx"

const { Title } = Typography

const ReportEditPage = ({
  report,
  reports,
  getReportDetails,
  getReports,
  clearDetail,
  rename
}: ConnectedProps<typeof connector>) => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [leaving, setLeaving] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [showShare, setShowShare] = useState(false)
  const [showSchedule, setShowSchedule] = useState(false)

  const accessLevel = report?.access_level ?? "owner"
  const canEdit = accessLevel === "owner" || accessLevel === "edit"
  const canShare = accessLevel === "owner"

  useEffect(() => {
    if (!id) return
    getReportDetails(id)
    return () => {
      clearDetail()
      dispatch(reportCanvasReset())
    }
  }, [id, getReportDetails, clearDetail, dispatch])

  // This route lives outside ReportsShell, so a deep link never loads the list
  // the folder picker derives its options from.
  useEffect(() => {
    if (reports.length === 0) getReports({})
  }, [reports.length, getReports])

  useEffect(() => {
    if (!report) return
    dispatch(
      reportCanvasHydrate({
        reportId: report.id,
        definition: normalizeDefinition(report.definition)
      })
    )
  }, [report?.id])

  useEffect(() => {
    generateDocumentTitle(report?.name || t("reports.build.editTitle"))
  }, [report?.name, t])

  const definition = useAppSelector((s) => s.reportCanvas.definition)
  const folderOptions = useMemo(() => distinctFolderNames(reports), [reports])
  const handleSettingsSubmit = useReportSettingsSubmit(report?.id)
  const { saveStatus, saveError, retry, saveNow } = useReportCanvasAutosave(
    report?.id,
    !canEdit
  )

  const handleRename = (name: string) => {
    if (!report || !name.trim() || name === report.name) return
    rename({ id: report.id, name: name.trim(), previousName: report.name })
  }

  // Flush the pending autosave before leaving so the view page's GET can't win
  // the race against the final PATCH; on failure stay here and surface it.
  const goToView = async () => {
    if (!report) return
    setLeaving(true)
    try {
      const result = await saveNow()
      if (!result.ok) {
        message.error(t("reports.toolbar.saveError"))
        return
      }
      navigate(RoutesPaths.reports.view(report.id))
    } finally {
      setLeaving(false)
    }
  }

  // Access is unknown until the detail GET lands; gate before render so a
  // view-only user never sees a frame of editable chrome.
  if (!report) {
    return (
      <div className="grid size-full" data-testid="report-edit-loading">
        <Flex
          vertical
          gap="middle"
          className="max-h-[calc(100vh-88px)] overflow-hidden p-1 -m-1"
        >
          <Skeleton active title paragraph={{ rows: 4 }} />
        </Flex>
      </div>
    )
  }

  if (!canEdit) {
    return <Navigate to={RoutesPaths.reports.view(report.id)} replace />
  }

  return (
    <div className="grid size-full">
      <Flex
        vertical
        gap="middle"
        className="max-h-[calc(100vh-88px)] overflow-hidden p-1 -m-1"
      >
        <Flex align="center" gap="small" wrap="wrap">
          <ActionTooltip title={t("reports.build.done")}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={goToView}
              disabled={leaving}
              aria-label={t("reports.build.done")}
            />
          </ActionTooltip>
          <ReportIconGlyph
            iconKey={reportIconKeyOf(definition)}
            style={{ fontSize: 20, color: neutral.muted }}
          />
          <div className="min-w-0 flex-1">
            <Title
              level={4}
              className="m-0"
              style={{ insetInlineStart: 0, insetBlockStart: 0 }}
              editable={{
                icon: <EditOutlined aria-label={t("reports.build.renameAria")} />,
                tooltip: t("reports.build.renameAria"),
                onChange: handleRename,
                triggerType: ["icon", "text"]
              }}
              ellipsis={{ tooltip: report.name }}
            >
              {report.name}
            </Title>
          </div>
          <ReportSaveIndicator
            status={saveStatus}
            error={saveError}
            onRetry={retry}
          />
          <ActionTooltip title={t("reports.actions.settings")}>
            <Button
              icon={<SettingOutlined />}
              aria-label={t("reports.actions.settings")}
              onClick={() => setShowSettings(true)}
            />
          </ActionTooltip>
          <ReportShareScheduleActions
            iconOnly
            canShare={canShare}
            canSchedule={canEdit}
            onShare={() => setShowShare(true)}
            onSchedule={() => setShowSchedule(true)}
          />
          <Button type="primary" loading={leaving} onClick={goToView}>
            {t("reports.build.done")}
          </Button>
        </Flex>

        <div className="min-h-0 flex-1 overflow-hidden">
          <ReportBuildLayout />
        </div>
      </Flex>

      {showSettings && (
        <ReportSettingsModal
          report={report}
          folderOptions={folderOptions}
          onSubmit={handleSettingsSubmit}
          onClose={() => setShowSettings(false)}
        />
      )}

      {showShare && (
        <ReportShareModal
          reportId={report.id}
          owner={report.owner}
          initialVisibility={report.visibility}
          onClose={() => setShowShare(false)}
          onSaved={() => getReportDetails(report.id)}
        />
      )}

      {showSchedule && (
        <ReportScheduleDrawer
          reportId={report.id}
          onClose={() => setShowSchedule(false)}
        />
      )}
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  report: state.reports.detail,
  reports: state.reports.results
})

const mapDispatchToProps = {
  getReportDetails: fetchReportDetails,
  getReports: fetchReports,
  clearDetail: reportsClearDetail,
  rename: renameReport
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ReportEditPage)
